/**
 * ReportPrint — chrome-less magazine render used by the PDF service.
 *
 * func-axn-report-pdf drives headless Chromium at /reports/{id}/print after
 * injecting the full report payload as window.__REPORT_DATA__ via
 * evaluateOnNewDocument — so this route renders without any authenticated
 * API call (page routes are anonymous at SWA level; only /api/* is gated).
 *
 * Fallback: when opened by a signed-in analyst with no injected data (print
 * layout debugging), it fetches the report through the normal authed proxy.
 *
 * Template: the ops-configured layout rides in on that same payload as
 * report.resolvedTemplate (ops-api lifts it from the generation-time snapshot
 * in generationParamsJson). It is validated here and handed to
 * PdfReportDocument; anything missing or malformed degrades to
 * DEFAULT_PDF_TEMPLATE. __PDF_RENDER_META__ reports whichever template was
 * actually used, because the PDF service folds that identity into the artifact
 * hash and blob name.
 *
 * Readiness contract with the PDF service: once the report is rendered AND
 * document.fonts.ready has resolved AND every <img> has finished decoding,
 * the page sets window.__PRINT_READY__ = true. Chromium waits on that flag
 * before calling page.pdf().
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DEFAULT_PDF_TEMPLATE,
  PdfReportDocument,
  type PdfTemplateConfig,
} from '../components/reporting/pdf/PdfReportDocument';
import type { ReportTelemetry } from '../components/reporting/pdf/charts/PdfChartSections';
import { getReport } from '../services/reports';
import type { ReportDetail } from '../data/types';

declare global {
  interface Window {
    __REPORT_DATA__?: ReportDetail;
    __PRINT_READY__?: boolean;
    __PDF_RENDER_META__?: PdfRenderMeta;
  }
}

export interface PdfRenderMeta {
  templateId: string;
  templateVersion: number;
  pageCount: number;
  overflowErrors: Array<{ section: string; overflowPx: number }>;
  assetErrors: string[];
}

/**
 * The writing agent occasionally emits exotic hyphens (U+2010/U+2011) and
 * soft hyphens. Screen fonts fall back fine, but headless Linux Chromium has
 * no fallback glyph for them in the heading font — they render as tofu boxes
 * in the PDF ("month□over□month"). Normalise to plain hyphens, print only.
 */
function sanitizeForPrint<T>(v: T): T {
  if (typeof v === 'string') {
    return (
      v
        .replace(/[\u2010\u2011\u2212]/g, '-')
        .replace(/\u00ad/g, '')
        // Exotic spaces get the same treatment as the exotic hyphens above, and
        // for the same reason: no fallback glyph in the headless Linux heading
        // font, so they print as tofu. Microsoft ships them — Windermere's top
        // Secure Score action arrives from Graph with a leading U+202F narrow
        // no-break space, which would also have indented the line by a space
        // nobody typed. Normalising to a plain space cannot hurt a fixed-page
        // layout: it only ever adds a break opportunity, never removes one.
        .replace(/[\u00a0\u2007\u2008\u2009\u202f\u205f]/g, ' ')
        // Zero-width space and BOM only — pure noise. Deliberately NOT the
        // zero-width joiner: stripping that would take apart any emoji sequence
        // it holds together.
        .replace(/[\u200b\ufeff]/g, '')
        .trim() as unknown as T
    );
  }
  if (Array.isArray(v)) return v.map(sanitizeForPrint) as unknown as T;
  if (v && typeof v === 'object') {
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>).map(([k, x]) => [k, sanitizeForPrint(x)]),
    ) as unknown as T;
  }
  return v;
}

/**
 * Structural guard for the template config that rides in on the report payload.
 *
 * ops-api validates the config at publish, at resolve and again on the way out,
 * so a failure here means something genuinely unexpected. We still check rather
 * than cast: this drives a fail-closed fixed-page renderer, and a config with a
 * missing theme key or a short sectionOrder would render a subtly wrong PDF
 * rather than erroring. Keys come from DEFAULT_PDF_TEMPLATE so this stays in
 * step with the document if a section is ever added.
 *
 * Falls back rather than throwing: a template we can't trust should degrade to
 * the known-good default, and __PDF_RENDER_META__ then reports the default's
 * identity — so the artifact name always names the template actually rendered.
 */
function asPdfTemplate(value: unknown): PdfTemplateConfig | null {
  if (!value || typeof value !== 'object') return null;
  const c = value as Record<string, unknown>;
  if (typeof c.id !== 'string' || typeof c.version !== 'number') return null;

  const keys = DEFAULT_PDF_TEMPLATE.sectionOrder;
  const order = c.sectionOrder;
  if (!Array.isArray(order) || order.length !== keys.length) return null;
  if (!keys.every((k) => order.includes(k))) return null;

  for (const field of ['enabledSections', 'startOnNewPage'] as const) {
    const map = c[field];
    if (!map || typeof map !== 'object') return null;
    const m = map as Record<string, unknown>;
    if (!keys.every((k) => typeof m[k] === 'boolean')) return null;
  }

  const theme = c.theme;
  if (!theme || typeof theme !== 'object') return null;
  const t = theme as Record<string, unknown>;
  if (!Object.keys(DEFAULT_PDF_TEMPLATE.theme).every((k) => typeof t[k] === 'string')) return null;

  return c as unknown as PdfTemplateConfig;
}

export function ReportPrint() {
  const { reportId } = useParams<{ reportId: string }>();
  const [report, setReport] = useState<ReportDetail | null>(
    window.__REPORT_DATA__ ? sanitizeForPrint(window.__REPORT_DATA__) : null,
  );
  const [error, setError] = useState<string | null>(null);

  const template = useMemo(() => {
    const resolved = report ? asPdfTemplate(report.resolvedTemplate) : null;
    if (report?.resolvedTemplate && !resolved) {
      // Surfaced to the PDF service via its console listener.
      console.error('[ReportPrint] resolvedTemplate failed validation; using default template');
    }
    return resolved ?? DEFAULT_PDF_TEMPLATE;
  }, [report]);

  /**
   * Telemetry is additive: a malformed or absent snapshot must never fail the
   * render, because the renderer is fail-closed and an aborted PDF is far worse
   * than a chart section that honestly says it has no data.
   */
  const telemetry = useMemo<ReportTelemetry | null>(() => {
    const raw = report?.telemetry;
    if (!raw || typeof raw !== 'object') return null;
    const t = raw as Partial<ReportTelemetry>;
    if (!Array.isArray(t.metrics)) {
      console.error('[ReportPrint] telemetry snapshot malformed; chart sections will show no data');
      return null;
    }
    // `previous` is rebuilt field by field like the rest of this block. Spreading
    // `t` instead would work today and silently drop the next field someone adds
    // upstream — which is exactly what this shape check exists to prevent.
    const prev = t.previous;
    const previous =
      prev && typeof prev === 'object' && Array.isArray(prev.metrics)
        ? {
            periodStart: typeof prev.periodStart === 'string' ? prev.periodStart : '',
            periodEnd: typeof prev.periodEnd === 'string' ? prev.periodEnd : '',
            capturedAt: typeof prev.capturedAt === 'string' ? prev.capturedAt : null,
            metrics: prev.metrics,
          }
        : null;

    // Second field to need this treatment, after `previous` — a reminder that
    // anything added to the telemetry block upstream is invisible here until it
    // is named. The check is doing its job; it just has to be told.
    const h = t.history;
    const history =
      h && typeof h === 'object' && Array.isArray(h.secureScore)
        ? {
            secureScore: h.secureScore.filter(
              (pt) => !!pt && typeof pt.period === 'string' && typeof pt.percentage === 'number',
            ),
          }
        : null;

    return {
      periodStart: typeof t.periodStart === 'string' ? t.periodStart : '',
      periodEnd: typeof t.periodEnd === 'string' ? t.periodEnd : '',
      capturedAt: typeof t.capturedAt === 'string' ? t.capturedAt : null,
      metrics: t.metrics,
      hasData: t.hasData === true,
      previous,
      history,
    };
  }, [report]);

  useEffect(() => {
    if (report || !reportId) return;
    getReport(reportId).then((res) => {
      if (res.success && res.data) setReport(sanitizeForPrint(res.data));
      else setError(res.error ?? 'Failed to load report');
    });
  }, [report, reportId]);

  useEffect(() => {
    if (!report) return;
    let cancelled = false;
    (async () => {
      await document.fonts.ready;
      const images = Array.from(document.querySelectorAll('img'));
      await Promise.all(
        images.map((img) =>
          img.complete ? Promise.resolve() : img.decode().catch(() => undefined),
        ),
      );
      // Two frames let font swaps and decoded images settle before measuring.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(() => r(undefined))));
      if (cancelled) return;

      const pages = Array.from(document.querySelectorAll<HTMLElement>('[data-pdf-page]'));
      const overflowErrors = pages.flatMap((page) => {
        const overflowPx = Math.ceil(page.scrollHeight - page.clientHeight);
        return overflowPx > 2
          ? [{ section: page.dataset.pdfSection ?? 'unknown', overflowPx }]
          : [];
      });

      /**
       * Text running underneath the page footer.
       *
       * scrollHeight vs clientHeight cannot see this. The footer is
       * absolutely positioned and the content column clips, so content can
       * overrun it while the page still measures as fitting exactly — which is
       * how a digest footnote came to be printed straight through the footer
       * rule with every existing check green.
       *
       * Measured on leaf elements that actually carry text, not on the
       * container: the container's own box stops short of the child that
       * overflows it, which is the whole reason this was invisible. Decorative
       * and empty elements are skipped so a full-bleed rule can still reach the
       * page edge.
       *
       * Reported inside `overflowErrors` on purpose — the PDF service already
       * fails closed on that array, so this needs no change there — with the
       * cause named in `section` so the thrown message explains itself.
       */
      const collisionErrors = pages.flatMap((page) => {
        const footer = page.querySelector<HTMLElement>('.pdf-page-footer');
        const content = page.querySelector<HTMLElement>('.pdf-page__content');
        if (!footer || !content) return [];
        const footerTop = footer.getBoundingClientRect().top;
        let lowest = Number.NEGATIVE_INFINITY;
        content.querySelectorAll<HTMLElement>('*').forEach((el) => {
          if (el.children.length > 0) return;
          if (!(el.textContent ?? '').trim()) return;
          lowest = Math.max(lowest, el.getBoundingClientRect().bottom);
        });
        if (!Number.isFinite(lowest)) return [];
        const overflowPx = Math.ceil(lowest - footerTop);
        return overflowPx > 2
          ? [
              {
                section: `${page.dataset.pdfSection ?? 'unknown'} (text overlapping page footer)`,
                overflowPx,
              },
            ]
          : [];
      });
      const assetErrors = images
        .filter((img) => img.naturalWidth === 0)
        .map((img) => img.currentSrc || img.src || img.alt || 'unknown image');
      const layoutErrors = [...overflowErrors, ...collisionErrors];
      window.__PDF_RENDER_META__ = {
        templateId: template.id,
        templateVersion: template.version,
        pageCount: pages.length,
        overflowErrors: layoutErrors,
        assetErrors,
      };
      window.__PRINT_READY__ = layoutErrors.length === 0 && assetErrors.length === 0;
    })();
    return () => {
      cancelled = true;
    };
  }, [report, template]);

  if (error) {
    return <div style={{ padding: 32, fontFamily: 'sans-serif' }}>Error: {error}</div>;
  }
  if (!report) {
    return <div style={{ padding: 32, fontFamily: 'sans-serif' }}>Loading report…</div>;
  }

  return (
    <PdfReportDocument report={report} template={template} telemetry={telemetry} />
  );
}
